import type { QuizQuestionPartResponse, QuizQuestionWithPartsResponse } from "@/api/quiz.ts";
import type { FlowItem } from "@/types/flow.ts";

const FALLBACK_LOCALE = "en-GB";

const QUESTION_PART_TYPES = new Set(["questiontext", "questionimage", "questionimageurl"]);

const ANSWER_PART_TYPES = new Set([
  "correctansweroption",
  "correctansweroptionimageurl",
  "distractoroption",
  "distractoroptionimageurl",
]);

const QUESTION_FLOW_ITEM_GROUP_PATTERN =
  /^(?:TopicPathwayQuizQuestion|CheckInQuestion(?:Retry)?|CheckOutQuestion(?:Retry)?)_(\d+)(?:_|$)/i;

export interface QuestionContentPart {
  altText?: string;
  content: string;
  contentType: "Image" | "Text";
  imagePath?: string;
  partId: string;
}

export interface QuestionAnswerChoice {
  answerLetter: "A" | "B" | "C" | "D";
  parts: Array<QuestionContentPart>;
}

export interface QuestionPartsModel {
  answerChoices: Array<QuestionAnswerChoice>;
  parts: Array<QuestionContentPart>;
}

export type QuestionsById = ReadonlyMap<number, QuizQuestionWithPartsResponse>;

export const getQuestionIdFromFlowItem = (flowItem: Partial<FlowItem>): number | null => {
  if (flowItem.flowItemType !== "PinnedImage") return null;

  const questionId = Number(
    String(flowItem.flowItemGroup ?? "").match(QUESTION_FLOW_ITEM_GROUP_PATTERN)?.[1],
  );

  return Number.isInteger(questionId) && questionId > 0 ? questionId : null;
};

export const buildQuestionPartsModel = (
  question: QuizQuestionWithPartsResponse | undefined,
  selectedLocale = FALLBACK_LOCALE,
): QuestionPartsModel => {
  const questionParts = selectPartsForLocale(
    Array.isArray(question?.questionParts) ? question.questionParts : [],
    selectedLocale,
  );
  const answerValues = questionParts.filter(
    (part) => normalisePartType(part.type) === "answervalue",
  );
  const rootParts = questionParts
    .filter((part) => !part.parentPartId)
    .sort(answerValues.length ? compareSequence : compareParts);
  const childParts = questionParts.filter((part) => part.parentPartId);
  const parts = rootParts
    .filter((part) => QUESTION_PART_TYPES.has(normalisePartType(part.type)))
    .map((part) => mapPart(part, childParts));
  const answerParts = rootParts.filter((part) =>
    ANSWER_PART_TYPES.has(normalisePartType(part.type)),
  );
  const answerPartsBySequence = answerValues.length
    ? groupAnswersByValue(answerParts, answerValues)
    : answerParts.reduce((partsBySequence, part) => {
        const sequence = Number(part.sequence);

        if (!Number.isInteger(sequence)) return partsBySequence;

        const partsForSequence = partsBySequence.get(sequence) ?? [];
        partsForSequence.push(part);
        partsBySequence.set(sequence, partsForSequence);
        return partsBySequence;
      }, new Map<number, Array<QuizQuestionPartResponse>>());
  const answerChoices = [...answerPartsBySequence.entries()]
    .sort(([leftSequence], [rightSequence]) => leftSequence - rightSequence)
    .flatMap(([sequence, answerParts]) => {
      const answerLetter = mapAnswerSequenceToLetter(sequence);

      return answerLetter
        ? [
            {
              answerLetter,
              parts: answerParts.map((part) => mapPart(part, childParts)),
            },
          ]
        : [];
    });

  return { answerChoices, parts };
};

const groupAnswersByValue = (
  answerParts: Array<QuizQuestionPartResponse>,
  answerValues: Array<QuizQuestionPartResponse>,
) => {
  const groups = new Map<number, Array<QuizQuestionPartResponse>>();
  let answerNumber: number | undefined;

  for (const part of answerParts) {
    const answerValue = answerValues.find(
      (child) => String(child.parentPartId) === String(part.partId),
    );
    // Only the first part of a multipart answer needs an ANSWER_VALUE.
    // Subsequent text or image parts belong to that same answer.
    if (answerValue) answerNumber = Number(answerValue.value);
    if (answerNumber === undefined) continue;

    const partsForAnswer = groups.get(answerNumber) ?? [];
    partsForAnswer.push(part);
    groups.set(answerNumber, partsForAnswer);
  }

  return groups;
};

const compareSequence = (left: QuizQuestionPartResponse, right: QuizQuestionPartResponse) =>
  getSortValue(left.sequence) - getSortValue(right.sequence) ||
  String(left.partId).localeCompare(String(right.partId));

export const hasRenderableQuestionParts = (question: QuizQuestionWithPartsResponse | undefined) => {
  const model = buildQuestionPartsModel(question);
  return model.parts.length > 0 && model.answerChoices.length > 0;
};

const selectPartsForLocale = (parts: Array<QuizQuestionPartResponse>, selectedLocale: string) => {
  if (!parts.length) return [];

  const localeParts = parts.filter((part) => part.locale === selectedLocale);
  if (localeParts.length) return localeParts;

  const fallbackParts = parts.filter((part) => part.locale === FALLBACK_LOCALE);
  if (fallbackParts.length) return fallbackParts;

  return parts.some((part) => part.locale) ? [] : parts;
};

const mapPart = (
  part: QuizQuestionPartResponse,
  childParts: Array<QuizQuestionPartResponse>,
): QuestionContentPart => {
  const contentType = normalisePartType(part.type).includes("image") ? "Image" : "Text";
  const altTextType = contentType === "Image" ? "imageurlalttext" : "alttext";
  const altText = childParts
    .find(
      (childPart) =>
        String(childPart.parentPartId) === String(part.partId) &&
        normalisePartType(childPart.type) === altTextType,
    )
    ?.value?.trim();

  return {
    altText: altText || undefined,
    content: contentType === "Text" ? (part.value ?? "") : "",
    contentType,
    imagePath: contentType === "Image" ? (part.value ?? "") : undefined,
    partId: String(part.partId),
  };
};

const compareParts = (left: QuizQuestionPartResponse, right: QuizQuestionPartResponse) =>
  getSortValue(left.row) - getSortValue(right.row) ||
  getSortValue(left.sequence) - getSortValue(right.sequence) ||
  String(left.partId).localeCompare(String(right.partId));

const getSortValue = (value: number | string | null) => {
  if (value === null || value === "") return Number.MAX_SAFE_INTEGER;

  const number = Number(value);
  return Number.isFinite(number) ? number : Number.MAX_SAFE_INTEGER;
};

const mapAnswerSequenceToLetter = (
  sequence: number,
): QuestionAnswerChoice["answerLetter"] | null => {
  switch (sequence) {
    case 1:
      return "A";
    case 2:
      return "B";
    case 3:
      return "C";
    case 4:
      return "D";
    default:
      return null;
  }
};

const normalisePartType = (type: string) => type.toLowerCase().replace(/[^a-z0-9]/g, "");
